import { definePlatformSetting, } from '@crest/platform-contracts/settings';
import { z } from 'zod';
import { timesheetsBillingCodeSchema, timesheetsClockBreakTypeSchema, timesheetsEntryKindSchema, } from '../contracts/timesheets-clock.contract.js';
import { timesheetsPayrollBasisPointsSchema, timesheetsPayrollCurrencyCodeSchema, timesheetsPayrollIsoWeekdaySchema, timesheetsPayrollMoneyCentsSchema, timesheetsPayrollSecondsSchema, timesheetsPayrollTimeZoneSchema, } from '../contracts/timesheets-payroll-cost-summary.contract.js';
import { timesheetsLocationPointSourceSchema, timesheetsLocationReasonSchema, timesheetsLocationSignalQualitySchema, timesheetsManagedPlaceKindSchema, } from '../contracts/timesheets-location-evidence.contract.js';
const preserveTimesheetsSettingValue = (value) => value;
function migrateTimesheetsActivityReviewCopyVersionOne(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return value;
    const record = value;
    if (record.navigationLabel !== 'Activity review')
        return value;
    return {
        ...record,
        navigationLabel: 'Timesheets',
    };
}
function supportedTimeZone(value) {
    try {
        new Intl.DateTimeFormat('en-US', { timeZone: value }).format(new Date(0));
        return true;
    }
    catch {
        return false;
    }
}
export const timesheetsWorkdayPolicyValueSchema = z.strictObject({
    timeZone: z.string().trim().min(1).max(100).refine(supportedTimeZone, 'A supported IANA time zone is required.'),
    lunchRequired: z.boolean(),
    lunchCorrectionEnabled: z.boolean(),
    dailyReportEnabled: z.boolean(),
    dailyReportLocalTime: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/),
    dailyReportWindowMinutes: z.number().int().min(15).max(180),
    dailyReportRecipientRoleKeys: z.array(z.string().regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/).max(80)).min(1).max(10).refine((roleKeys) => new Set(roleKeys).size === roleKeys.length, 'Daily-report recipient role keys must be unique.'),
});
export const timesheetsWorkdayPolicySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.workday.policy',
        moduleId: 'timesheets',
        title: 'Timesheets workday and lunch policy',
        helpText: 'Controls the tenant time zone, missing-lunch workflow, and protected report intent. Automatic scheduling and delivery require the separate worker and Communications capabilities.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            timeZone: 'America/Indiana/Indianapolis',
            lunchRequired: true,
            lunchCorrectionEnabled: true,
            dailyReportEnabled: true,
            dailyReportLocalTime: '20:15',
            dailyReportWindowMinutes: 45,
            dailyReportRecipientRoleKeys: ['owner', 'manager'],
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'protected',
    },
    valueSchema: timesheetsWorkdayPolicyValueSchema,
});
export const timesheetsPayrollPolicyValueSchema = z.strictObject({
    effectiveFrom: z.string().datetime({ offset: true }),
    currencyCode: timesheetsPayrollCurrencyCodeSchema,
    timeZone: timesheetsPayrollTimeZoneSchema,
    weekStartsOnIsoDay: timesheetsPayrollIsoWeekdaySchema,
    overtimeThresholdSeconds: timesheetsPayrollSecondsSchema.min(1).max(604_800),
    overtimeMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.min(10_000),
    ordinaryMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.min(1),
    hazardMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.min(1),
    afterHoursMultiplierBasisPoints: timesheetsPayrollBasisPointsSchema.min(1),
    afterHoursCalloutExclusionSeconds: timesheetsPayrollSecondsSchema.max(86_400),
    afterHoursCalloutBonusCents: timesheetsPayrollMoneyCentsSchema,
    afterHoursStartLocalHour: z.number().int().min(0).max(23),
    afterHoursEndLocalHour: z.number().int().min(0).max(23),
    mileageRateMillsPerMile: z.number().int().min(0).max(1_000_000),
}).superRefine((policy, context) => {
    if (policy.afterHoursStartLocalHour === policy.afterHoursEndLocalHour) {
        context.addIssue({
            code: 'custom',
            path: ['afterHoursEndLocalHour'],
            message: 'The after-hours window cannot cover every hour of the day.',
        });
    }
    if (policy.hazardMultiplierBasisPoints < policy.ordinaryMultiplierBasisPoints) {
        context.addIssue({
            code: 'custom',
            path: ['hazardMultiplierBasisPoints'],
            message: 'The hazard multiplier cannot be lower than the ordinary multiplier.',
        });
    }
    if (policy.afterHoursMultiplierBasisPoints < policy.ordinaryMultiplierBasisPoints) {
        context.addIssue({
            code: 'custom',
            path: ['afterHoursMultiplierBasisPoints'],
            message: 'The after-hours multiplier cannot be lower than the ordinary multiplier.',
        });
    }
});
export const timesheetsPayrollPolicySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.payroll.policy',
        moduleId: 'timesheets',
        title: 'Timesheets payroll and mileage policy',
        helpText: 'Controls exact tenant payroll multipliers, overtime, callout, week, time-zone, and mileage rules. Published releases are snapshotted onto entries so later edits and rollbacks never rewrite historical pay.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            effectiveFrom: '1970-01-01T00:00:00.000Z',
            currencyCode: 'USD',
            timeZone: 'America/Indiana/Indianapolis',
            weekStartsOnIsoDay: 1,
            overtimeThresholdSeconds: 144_000,
            overtimeMultiplierBasisPoints: 15_000,
            ordinaryMultiplierBasisPoints: 10_000,
            hazardMultiplierBasisPoints: 15_000,
            afterHoursMultiplierBasisPoints: 15_000,
            afterHoursCalloutExclusionSeconds: 3_600,
            afterHoursCalloutBonusCents: 10_000,
            afterHoursStartLocalHour: 18,
            afterHoursEndLocalHour: 6,
            mileageRateMillsPerMile: 725,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'replace',
        clientSafe: false,
        serverOnly: true,
        sensitive: true,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'protected',
    },
    valueSchema: timesheetsPayrollPolicyValueSchema,
});
export const timesheetsClockDisplayValueSchema = z.strictObject({
    recentEntryCount: z.number().int().min(3).max(20),
    showSeconds: z.boolean(),
});
export const timesheetsClockDisplaySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.clock.display',
        moduleId: 'timesheets',
        title: 'Time Clock display',
        helpText: 'Controls tenant-wide Time Clock presentation without changing payroll rules.',
        configurationDomain: 'module',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            recentEntryCount: 8,
            showSeconds: false,
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsClockDisplayValueSchema,
});
export const timesheetsActivityReviewDisplayValueSchema = z.strictObject({
    defaultRangeDays: z.number().int().min(1).max(90),
    filterDensity: z.enum(['comfortable', 'compact']),
    showWindowSummary: z.boolean(),
});
export const timesheetsActivityReviewDisplaySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.activity-review.display',
        moduleId: 'timesheets',
        title: 'Timesheets activity-review display',
        helpText: 'Controls the Timesheets-owned employee and date-window host used by removable activity contributions.',
        configurationDomain: 'layout',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            defaultRangeDays: 7,
            filterDensity: 'comfortable',
            showWindowSummary: true,
        },
        permittedScopes: ['platform', 'plan', 'tenant', 'user'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: true,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsActivityReviewDisplayValueSchema,
});
export const timesheetsClockCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(80),
    description: z.string().trim().min(1).max(240),
    loadingStatus: z.string().trim().min(1).max(120),
    statusUnavailable: z.string().trim().min(1).max(240),
    clockedOutLabel: z.string().trim().min(1).max(120),
    clockedInLabel: z.string().trim().min(1).max(120),
    clockInAction: z.string().trim().min(1).max(80),
    clockOutAction: z.string().trim().min(1).max(80),
    workTypeLabel: z.string().trim().min(1).max(80),
    jobSiteLabel: z.string().trim().min(1).max(80),
    shopTimeLabel: z.string().trim().min(1).max(80),
    projectSearchLabel: z.string().trim().min(1).max(80),
    projectSearchPlaceholder: z.string().trim().min(1).max(120),
    projectSelectLabel: z.string().trim().min(1).max(80),
    projectSelectPlaceholder: z.string().trim().min(1).max(80),
    projectRequiredHelp: z.string().trim().min(1).max(180),
    noMatchingProjectsLabel: z.string().trim().min(1).max(160),
    notesLabel: z.string().trim().min(1).max(80),
    notesHelp: z.string().trim().min(1).max(180),
    shopNotesHelp: z.string().trim().min(1).max(180),
    startedAtLabel: z.string().trim().min(1).max(80),
    recentEntriesTitle: z.string().trim().min(1).max(120),
    recentEntriesEmpty: z.string().trim().min(1).max(180),
    completedLabel: z.string().trim().min(1).max(80),
    needsCorrectionLabel: z.string().trim().min(1).max(120),
    needsCorrectionHelp: z.string().trim().min(1).max(240),
    lunchPolicyLabel: z.string().trim().min(1).max(180),
    startLunchAction: z.string().trim().min(1).max(80),
    endLunchAction: z.string().trim().min(1).max(80),
    endBreakAction: z.string().trim().min(1).max(80),
    missingLunchTitle: z.string().trim().min(1).max(120),
    missingLunchDescription: z.string().trim().min(1).max(320),
    lunchClockOutLabel: z.string().trim().min(1).max(100),
    lunchClockInLabel: z.string().trim().min(1).max(100),
    lunchCorrectionHelp: z.string().trim().min(1).max(320),
    lunchCorrectionSubmitAction: z.string().trim().min(1).max(100),
    lunchCorrectionPendingLabel: z.string().trim().min(1).max(240),
    lunchCorrectionDismissAction: z.string().trim().min(1).max(80),
    lunchCorrectionCloseAction: z.string().trim().min(1).max(80),
    lunchCorrectionSuccess: z.string().trim().min(1).max(240),
    lunchCorrectionError: z.string().trim().min(1).max(240),
    mutationError: z.string().trim().min(1).max(240),
});
export const timesheetsMyDayCopyValueSchema = z.strictObject({
    panelTitle: z.string().trim().min(1).max(120),
    clockedInDescription: z.string().trim().min(1).max(200),
    clockedOutDescription: z.string().trim().min(1).max(200),
    currentProjectLabel: z.string().trim().min(1).max(80),
    nextProjectLabel: z.string().trim().min(1).max(80),
    startedAtLabel: z.string().trim().min(1).max(80),
    needsCorrectionLabel: z.string().trim().min(1).max(160),
    openClockAction: z.string().trim().min(1).max(80),
    openProjectAction: z.string().trim().min(1).max(80),
    loadingLabel: z.string().trim().min(1).max(120),
    statusUnavailable: z.string().trim().min(1).max(240),
});
const timesheetsClockCopyCodedFallback = {
    navigationLabel: 'Time Clock',
    title: 'Time Clock',
    description: 'Record job-site or shop time with an auditable device and server timestamp.',
    loadingStatus: 'Checking your clock status…',
    statusUnavailable: 'Clock status is unavailable. No clock action is allowed until it reloads.',
    clockedOutLabel: 'Not clocked in',
    clockedInLabel: 'Clocked in',
    clockInAction: 'Clock in',
    clockOutAction: 'Clock out',
    workTypeLabel: 'Work type',
    jobSiteLabel: 'Job site',
    shopTimeLabel: 'Shop time',
    projectSearchLabel: 'Find a Project',
    projectSearchPlaceholder: 'Search by Project number or name',
    projectSelectLabel: 'Project',
    projectSelectPlaceholder: 'Choose a Project',
    projectRequiredHelp: 'Job-site time needs a Project. Choose Shop time for shop, office, or training work.',
    noMatchingProjectsLabel: 'No Projects match that search.',
    notesLabel: 'Work notes',
    notesHelp: 'Optional. Add anything a manager should know about this time.',
    shopNotesHelp: 'Required for shop time. Describe the shop, office, training, or other administrative work.',
    startedAtLabel: 'Started',
    recentEntriesTitle: 'Recent entries',
    recentEntriesEmpty: 'No time entries have been recorded yet.',
    completedLabel: 'Completed',
    needsCorrectionLabel: 'Manager correction required',
    needsCorrectionHelp: 'A shift exceeded the safe open-time limit. CREST retained it at zero verified duration for review.',
    lunchPolicyLabel: 'Employees must clock out for lunch.',
    startLunchAction: 'Lunch clock-out',
    endLunchAction: 'Back from lunch',
    endBreakAction: 'End break',
    missingLunchTitle: 'Lunch break missing',
    missingLunchDescription: 'No lunch clock-out is recorded for this work date. Enter the actual lunch times to send a manager-approved correction.',
    lunchClockOutLabel: 'Lunch clock-out',
    lunchClockInLabel: 'Back from lunch',
    lunchCorrectionHelp: 'This request can only add a lunch break. It cannot change Projects, work types, notes, or other timesheet fields.',
    lunchCorrectionSubmitAction: 'Send lunch correction request',
    lunchCorrectionPendingLabel: 'A lunch correction for this date is already waiting for manager approval.',
    lunchCorrectionDismissAction: 'Not now',
    lunchCorrectionCloseAction: 'Close',
    lunchCorrectionSuccess: 'Lunch correction request sent to a manager.',
    lunchCorrectionError: 'The lunch correction request could not be sent.',
    mutationError: 'The clock action could not be completed. Reload the status before trying again.',
};
function migrateTimesheetsClockCopyVersionTwo(value) {
    if (typeof value !== 'object' || value === null || Array.isArray(value))
        return value;
    const record = value;
    return {
        ...timesheetsClockCopyCodedFallback,
        ...record,
    };
}
export const timesheetsClockCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.clock',
        moduleId: 'timesheets',
        title: 'Time Clock interface copy',
        helpText: 'Controls tenant-editable labels on the Timesheets clock screen.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 3,
        codedFallback: timesheetsClockCopyCodedFallback,
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsClockCopyValueSchema,
    migrations: [
        { fromVersion: 1, toVersion: 2, migrate: preserveTimesheetsSettingValue },
        { fromVersion: 2, toVersion: 3, migrate: migrateTimesheetsClockCopyVersionTwo },
    ],
});
export const timesheetsMyDayCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.my-day',
        moduleId: 'timesheets',
        title: 'My Day time and current-job copy',
        helpText: 'Controls tenant-editable labels for the Timesheets panel on My Day.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            panelTitle: 'Time and current job',
            clockedInDescription: 'You are clocked in. Keep the current job and elapsed time visible.',
            clockedOutDescription: 'You are not clocked in. Start from the correct job before work begins.',
            currentProjectLabel: 'Current job',
            nextProjectLabel: 'Suggested next job',
            startedAtLabel: 'Started',
            needsCorrectionLabel: 'A time entry needs manager correction.',
            openClockAction: 'Open Time Clock',
            openProjectAction: 'Open job',
            loadingLabel: 'Checking your time and current job…',
            statusUnavailable: 'Time and current-job status is unavailable. Open Time Clock to reload it.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsMyDayCopyValueSchema,
});
export const timesheetsLunchReviewCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(280),
    pendingFilterLabel: z.string().trim().min(1).max(80),
    allFilterLabel: z.string().trim().min(1).max(80),
    emptyLabel: z.string().trim().min(1).max(200),
    loadingLabel: z.string().trim().min(1).max(120),
    loadErrorLabel: z.string().trim().min(1).max(240),
    employeeLabel: z.string().trim().min(1).max(80),
    unknownEmployeeLabel: z.string().trim().min(1).max(120),
    requestedAtLabel: z.string().trim().min(1).max(80),
    statusLabel: z.string().trim().min(1).max(80),
    pendingStatusLabel: z.string().trim().min(1).max(80),
    approvedStatusLabel: z.string().trim().min(1).max(80),
    rejectedStatusLabel: z.string().trim().min(1).max(80),
    lunchClockOutLabel: z.string().trim().min(1).max(100),
    lunchClockInLabel: z.string().trim().min(1).max(100),
    requestReasonLabel: z.string().trim().min(1).max(100),
    approveAction: z.string().trim().min(1).max(80),
    rejectAction: z.string().trim().min(1).max(80),
    rejectionNoteLabel: z.string().trim().min(1).max(120),
    selfReviewBlockedLabel: z.string().trim().min(1).max(240),
    reviewErrorLabel: z.string().trim().min(1).max(240),
    reportTitle: z.string().trim().min(1).max(120),
    reportDescription: z.string().trim().min(1).max(280),
    workDateLabel: z.string().trim().min(1).max(80),
    previewReportAction: z.string().trim().min(1).max(100),
    queueReportAction: z.string().trim().min(1).max(100),
    reportQueuedLabel: z.string().trim().min(1).max(240),
    reportErrorLabel: z.string().trim().min(1).max(240),
    missingCountLabel: z.string().trim().min(1).max(100),
    pendingCountLabel: z.string().trim().min(1).max(100),
    openCountLabel: z.string().trim().min(1).max(100),
    reportEmptyLabel: z.string().trim().min(1).max(200),
    firstClockInLabel: z.string().trim().min(1).max(100),
    finalClockOutLabel: z.string().trim().min(1).max(100),
    openEntryLabel: z.string().trim().min(1).max(100),
});
export const timesheetsLunchReviewCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.lunch-review',
        moduleId: 'timesheets',
        title: 'Missing-lunch review copy',
        helpText: 'Controls tenant-editable labels for manager lunch correction review and report preview.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Lunch review',
            title: 'Missing-lunch review',
            description: 'Review employee lunch requests before any retained timesheet row changes.',
            pendingFilterLabel: 'Pending requests',
            allFilterLabel: 'All requests',
            emptyLabel: 'No lunch correction requests match this view.',
            loadingLabel: 'Loading lunch corrections…',
            loadErrorLabel: 'Lunch corrections are unavailable.',
            employeeLabel: 'Employee',
            unknownEmployeeLabel: 'Former or unavailable team member',
            requestedAtLabel: 'Requested',
            statusLabel: 'Status',
            pendingStatusLabel: 'Pending',
            approvedStatusLabel: 'Approved',
            rejectedStatusLabel: 'Rejected',
            lunchClockOutLabel: 'Lunch clock-out',
            lunchClockInLabel: 'Back from lunch',
            requestReasonLabel: 'Employee reason',
            approveAction: 'Approve atomic split',
            rejectAction: 'Reject request',
            rejectionNoteLabel: 'Rejection explanation',
            selfReviewBlockedLabel: 'The employee who requested this correction cannot approve or reject it.',
            reviewErrorLabel: 'The lunch correction was not reviewed. Reload before trying again.',
            reportTitle: 'End-of-day missing-lunch report',
            reportDescription: 'Preview and queue one provider-neutral owner/manager report fact for a tenant work date.',
            workDateLabel: 'Work date',
            previewReportAction: 'Preview report',
            queueReportAction: 'Create owner/manager report fact',
            reportQueuedLabel: 'The provider-neutral report fact was created. Automatic scheduling, recipient resolution, and delivery remain separate worker capabilities.',
            reportErrorLabel: 'The missing-lunch report could not be prepared.',
            missingCountLabel: 'Missing lunches',
            pendingCountLabel: 'Corrections pending',
            openCountLabel: 'Open entries',
            reportEmptyLabel: 'No team members are missing a lunch for this work date.',
            firstClockInLabel: 'First clock-in',
            finalClockOutLabel: 'Final clock-out',
            openEntryLabel: 'Entry still open',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsLunchReviewCopyValueSchema,
});
export const timesheetsCrewAllocationCopyValueSchema = z.strictObject({
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(280),
    loadingLabel: z.string().trim().min(1).max(120),
    loadErrorLabel: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(200),
    primaryLabel: z.string().trim().min(1).max(80),
    helperLabel: z.string().trim().min(1).max(80),
    effectiveAtLabel: z.string().trim().min(1).max(100),
    notesLabel: z.string().trim().min(1).max(100),
    requestAction: z.string().trim().min(1).max(100),
    managerOverrideLabel: z.string().trim().min(1).max(120),
    managerOverrideReasonLabel: z.string().trim().min(1).max(140),
    confirmAction: z.string().trim().min(1).max(80),
    rejectAction: z.string().trim().min(1).max(80),
    endAction: z.string().trim().min(1).max(80),
    rejectionReasonLabel: z.string().trim().min(1).max(120),
    endReasonLabel: z.string().trim().min(1).max(120),
    pendingStatusLabel: z.string().trim().min(1).max(80),
    confirmedStatusLabel: z.string().trim().min(1).max(80),
    rejectedStatusLabel: z.string().trim().min(1).max(80),
    endedStatusLabel: z.string().trim().min(1).max(80),
    cancelledStatusLabel: z.string().trim().min(1).max(80),
    awaitingYouLabel: z.string().trim().min(1).max(160),
    unknownMemberLabel: z.string().trim().min(1).max(120),
    mutationErrorLabel: z.string().trim().min(1).max(240),
    mutationSuccessLabel: z.string().trim().min(1).max(200),
});
export const timesheetsCrewAllocationCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.crew-allocation',
        moduleId: 'timesheets',
        title: 'Timesheets crew-allocation copy',
        helpText: 'Controls tenant-editable labels for primary/helper pairing on the Time Clock.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            title: 'Crew pairing',
            description: 'Pair a helper with a primary technician while each employee keeps an independent clock boundary.',
            loadingLabel: 'Loading crew assignments…',
            loadErrorLabel: 'Crew assignments are unavailable.',
            emptyLabel: 'No active crew assignments.',
            primaryLabel: 'Primary technician',
            helperLabel: 'Helper technician',
            effectiveAtLabel: 'Pairing starts',
            notesLabel: 'Pairing notes',
            requestAction: 'Request crew pairing',
            managerOverrideLabel: 'Confirm immediately as manager',
            managerOverrideReasonLabel: 'Manager override reason',
            confirmAction: 'Confirm pairing',
            rejectAction: 'Reject pairing',
            endAction: 'End pairing',
            rejectionReasonLabel: 'Rejection reason',
            endReasonLabel: 'End reason',
            pendingStatusLabel: 'Pending confirmation',
            confirmedStatusLabel: 'Confirmed',
            rejectedStatusLabel: 'Rejected',
            endedStatusLabel: 'Ended',
            cancelledStatusLabel: 'Cancelled',
            awaitingYouLabel: 'Your confirmation is required.',
            unknownMemberLabel: 'Unavailable team member',
            mutationErrorLabel: 'The crew pairing was not changed. Reload before trying again.',
            mutationSuccessLabel: 'Crew pairing updated.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsCrewAllocationCopyValueSchema,
});
export const timesheetsLocationReviewCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(320),
    boundaryNotice: z.string().trim().min(1).max(400),
    loadingLabel: z.string().trim().min(1).max(120),
    loadErrorLabel: z.string().trim().min(1).max(240),
    pointsTitle: z.string().trim().min(1).max(120),
    pointsDescription: z.string().trim().min(1).max(280),
    emptyPointsLabel: z.string().trim().min(1).max(200),
    visitsTitle: z.string().trim().min(1).max(120),
    visitsDescription: z.string().trim().min(1).max(280),
    emptyVisitsLabel: z.string().trim().min(1).max(200),
    managedPlacesTitle: z.string().trim().min(1).max(120),
    managedPlacesDescription: z.string().trim().min(1).max(280),
    emptyPlacesLabel: z.string().trim().min(1).max(200),
    employeeLabel: z.string().trim().min(1).max(80),
    unknownEmployeeLabel: z.string().trim().min(1).max(120),
    observedLabel: z.string().trim().min(1).max(80),
    sourceLabel: z.string().trim().min(1).max(80),
    qualityLabel: z.string().trim().min(1).max(80),
    reasonsLabel: z.string().trim().min(1).max(80),
    currentProjectLabel: z.string().trim().min(1).max(100),
    matchedProjectsLabel: z.string().trim().min(1).max(100),
    unknownProjectLabel: z.string().trim().min(1).max(140),
    noProjectMatchLabel: z.string().trim().min(1).max(140),
    coordinatesLabel: z.string().trim().min(1).max(80),
    accuracyLabel: z.string().trim().min(1).max(100),
    accuracyUnavailableLabel: z.string().trim().min(1).max(120),
    pointCountLabel: z.string().trim().min(1).max(80),
    reviewReasonLabel: z.string().trim().min(1).max(120),
    acceptAction: z.string().trim().min(1).max(100),
    excludeAction: z.string().trim().min(1).max(100),
    selfReviewBlockedLabel: z.string().trim().min(1).max(240),
    reviewErrorLabel: z.string().trim().min(1).max(240),
    placeKindLabel: z.string().trim().min(1).max(80),
    placeLabelLabel: z.string().trim().min(1).max(80),
    latitudeLabel: z.string().trim().min(1).max(80),
    longitudeLabel: z.string().trim().min(1).max(80),
    radiusFeetLabel: z.string().trim().min(1).max(100),
    createPlaceAction: z.string().trim().min(1).max(100),
    activeStatusLabel: z.string().trim().min(1).max(80),
    inactiveStatusLabel: z.string().trim().min(1).max(80),
    deactivateAction: z.string().trim().min(1).max(100),
    reactivateAction: z.string().trim().min(1).max(100),
    placeErrorLabel: z.string().trim().min(1).max(240),
    mutationSuccessLabel: z.string().trim().min(1).max(200),
    reasonLabels: z.record(timesheetsLocationReasonSchema, z.string().trim().min(1).max(140)),
    sourceLabels: z.record(timesheetsLocationPointSourceSchema, z.string().trim().min(1).max(120)),
    qualityLabels: z.record(timesheetsLocationSignalQualitySchema, z.string().trim().min(1).max(120)),
    managedPlaceKindLabels: z.record(timesheetsManagedPlaceKindSchema, z.string().trim().min(1).max(120)),
});
export const timesheetsLocationReviewCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.location-review',
        moduleId: 'timesheets',
        title: 'Timesheets location-evidence review copy',
        helpText: 'Controls tenant-editable manager labels for location evidence, visit candidates, and managed places. Copy cannot make location evidence authoritative for payroll.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Location review',
            title: 'Location evidence review',
            description: 'Review employee location observations and possible Project visits, and maintain company-managed places.',
            boundaryNotice: 'Location is supporting evidence only. Accepting it does not clock an employee in or out, split time, approve payroll, or replace a manager-reviewed timesheet correction.',
            loadingLabel: 'Loading location evidence…',
            loadErrorLabel: 'Location evidence is unavailable. No review action was taken.',
            pointsTitle: 'Observations needing review',
            pointsDescription: 'Device observations that CREST retained but could not automatically trust.',
            emptyPointsLabel: 'No location observations are waiting for review.',
            visitsTitle: 'Possible Project visits',
            visitsDescription: 'Ten-minute Project dwell candidates assembled from retained observations.',
            emptyVisitsLabel: 'No possible Project visits are waiting for review.',
            managedPlacesTitle: 'Managed places',
            managedPlacesDescription: 'Maintain company shops, warehouses, vendors, and other locations that should not become Project visit candidates.',
            emptyPlacesLabel: 'No managed places have been configured.',
            employeeLabel: 'Employee',
            unknownEmployeeLabel: 'Former or unavailable team member',
            observedLabel: 'Observed',
            sourceLabel: 'Capture source',
            qualityLabel: 'Signal quality',
            reasonsLabel: 'Why review is required',
            currentProjectLabel: 'Clocked Project',
            matchedProjectsLabel: 'Location-matched Projects',
            unknownProjectLabel: 'Unavailable Project',
            noProjectMatchLabel: 'No Project location matched',
            coordinatesLabel: 'Observed location',
            accuracyLabel: 'Estimated accuracy in feet',
            accuracyUnavailableLabel: 'Not supplied',
            pointCountLabel: 'Observations',
            reviewReasonLabel: 'Manager review note',
            acceptAction: 'Accept as supporting evidence',
            excludeAction: 'Exclude evidence',
            selfReviewBlockedLabel: 'Employees cannot review their own location evidence.',
            reviewErrorLabel: 'The evidence was not reviewed. Reload before trying again.',
            placeKindLabel: 'Place type',
            placeLabelLabel: 'Place name',
            latitudeLabel: 'Latitude',
            longitudeLabel: 'Longitude',
            radiusFeetLabel: 'Boundary radius in feet',
            createPlaceAction: 'Add managed place',
            activeStatusLabel: 'Active',
            inactiveStatusLabel: 'Inactive',
            deactivateAction: 'Deactivate place',
            reactivateAction: 'Reactivate place',
            placeErrorLabel: 'The managed place was not changed. Check the values and reload before trying again.',
            mutationSuccessLabel: 'Location review settings updated.',
            reasonLabels: {
                'device-not-attested': 'Device identity is registered but not attested',
                'accuracy-missing': 'Accuracy was not supplied',
                'accuracy-low': 'Location accuracy was too low',
                'offline-replay': 'Observation was replayed offline',
                'receipt-delayed': 'Server receipt was delayed',
                'receipt-expired': 'Observation arrived outside the trusted receipt window',
                'occurred-in-future': 'Device time was ahead of server time',
                'outside-entry-window': 'Observation falls outside the retained time-entry window',
                'entry-needs-correction': 'The related time entry needs correction',
                'out-of-order': 'A later observation had already been received',
                'impossible-path': 'Travel speed between observations was impossible',
                'managed-place-overlap': 'More than one managed place matched',
                'project-match-ambiguous': 'More than one Project location matched',
                'project-location-unavailable': 'The related Project has no active time location',
                'dwell-insufficient': 'The Project dwell interval was too short',
                'dwell-gap-too-large': 'The gap between observations was too large',
                'latest-point-stale': 'The most recent observation was stale',
            },
            sourceLabels: {
                'clock-boundary': 'Clock action',
                periodic: 'Periodic background observation',
                'geofence-transition': 'Region transition',
                'offline-replay': 'Offline replay',
            },
            qualityLabels: {
                strong: 'Strong',
                usable: 'Usable',
                low: 'Low',
                missing: 'Not supplied',
            },
            managedPlaceKindLabels: {
                shop: 'Shop',
                warehouse: 'Warehouse',
                vendor: 'Vendor',
                'material-supplier': 'Material supplier',
                other: 'Other',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsLocationReviewCopyValueSchema,
});
export const timesheetsEntrySplitCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(320),
    boundaryNotice: z.string().trim().min(1).max(360),
    loadingLabel: z.string().trim().min(1).max(120),
    loadErrorLabel: z.string().trim().min(1).max(240),
    emptyLabel: z.string().trim().min(1).max(240),
    entryLabel: z.string().trim().min(1).max(100),
    employeeLabel: z.string().trim().min(1).max(80),
    unknownEmployeeLabel: z.string().trim().min(1).max(120),
    originalWindowLabel: z.string().trim().min(1).max(100),
    revisionLabel: z.string().trim().min(1).max(80),
    segmentLabel: z.string().trim().min(1).max(80),
    startLabel: z.string().trim().min(1).max(80),
    endLabel: z.string().trim().min(1).max(80),
    kindLabel: z.string().trim().min(1).max(80),
    breakTypeLabel: z.string().trim().min(1).max(80),
    billingCodeLabel: z.string().trim().min(1).max(80),
    projectLabel: z.string().trim().min(1).max(80),
    noProjectLabel: z.string().trim().min(1).max(120),
    notesLabel: z.string().trim().min(1).max(80),
    reasonLabel: z.string().trim().min(1).max(80),
    reasonHelp: z.string().trim().min(1).max(240),
    addSegmentAction: z.string().trim().min(1).max(100),
    removeSegmentAction: z.string().trim().min(1).max(100),
    submitAction: z.string().trim().min(1).max(100),
    submittingLabel: z.string().trim().min(1).max(100),
    successLabel: z.string().trim().min(1).max(240),
    idempotentLabel: z.string().trim().min(1).max(240),
    mutationErrorLabel: z.string().trim().min(1).max(280),
    payBasisAvailableLabel: z.string().trim().min(1).max(120),
    entryKindLabels: z.record(timesheetsEntryKindSchema, z.string().trim().min(1).max(100)),
    breakTypeLabels: z.record(timesheetsClockBreakTypeSchema, z.string().trim().min(1).max(100)),
    billingCodeLabels: z.record(timesheetsBillingCodeSchema, z.string().trim().min(1).max(120)),
});
export const timesheetsEntrySplitCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.entry-split',
        moduleId: 'timesheets',
        title: 'Timesheets entry-split copy',
        helpText: 'Controls tenant-editable manager labels for splitting one retained entry into exact work and break segments. Copy cannot bypass revision, pay-basis, audit, or review controls.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 1,
        codedFallback: {
            navigationLabel: 'Split time entries',
            title: 'Split a time entry',
            description: 'Reconcile one retained entry into exact work, break, Project, and billing segments without deleting its original identity.',
            boundaryNotice: 'The first clock-in and final clock-out stay fixed. Every second must be assigned once. New segments return to pending review and receive an immutable pay basis; this action does not approve payroll.',
            loadingLabel: 'Loading retained time entries…',
            loadErrorLabel: 'Time entries are unavailable. No split action was taken.',
            emptyLabel: 'No completed retained entries are available to split.',
            entryLabel: 'Retained time entry',
            employeeLabel: 'Employee',
            unknownEmployeeLabel: 'Former or unavailable team member',
            originalWindowLabel: 'Original retained window',
            revisionLabel: 'Revision',
            segmentLabel: 'Segment',
            startLabel: 'Start',
            endLabel: 'End',
            kindLabel: 'Time type',
            breakTypeLabel: 'Break type',
            billingCodeLabel: 'Billing and pay type',
            projectLabel: 'Project',
            noProjectLabel: 'Shop or administrative time',
            notesLabel: 'Segment notes',
            reasonLabel: 'Manager adjustment reason',
            reasonHelp: 'Explain the source records used to reconcile this entry. At least five characters are required.',
            addSegmentAction: 'Split this segment',
            removeSegmentAction: 'Remove segment',
            submitAction: 'Apply atomic split',
            submittingLabel: 'Applying split…',
            successLabel: 'The entry was split and every segment was returned to pending review.',
            idempotentLabel: 'This exact split command was already applied; CREST returned the original result.',
            mutationErrorLabel: 'The entry was not split. Reload it and verify every boundary, Project, pay basis, and manager reason.',
            payBasisAvailableLabel: 'Pay basis captured',
            entryKindLabels: { work: 'Work', break: 'Break' },
            breakTypeLabels: {
                lunch: 'Lunch',
                rest: 'Rest break',
                personal: 'Personal break',
                other: 'Other break',
            },
            billingCodeLabels: {
                general: 'General',
                after_hours: 'After hours',
                emergency: 'Emergency',
                biohazard: 'PPE / biohazard',
                on_call: 'On call',
                drive_time: 'Drive time',
                admin: 'Administrative',
                prep: 'Preparation',
                break: 'Break',
            },
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: true,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsEntrySplitCopyValueSchema,
});
export const timesheetsActivityReviewCopyValueSchema = z.strictObject({
    navigationLabel: z.string().trim().min(1).max(60),
    title: z.string().trim().min(1).max(100),
    description: z.string().trim().min(1).max(320),
    employeeLabel: z.string().trim().min(1).max(100),
    fromLabel: z.string().trim().min(1).max(100),
    toLabel: z.string().trim().min(1).max(100),
    selectedEmployeeLabel: z.string().trim().min(1).max(100),
    selectedWindowLabel: z.string().trim().min(1).max(100),
    loadingMemberships: z.string().trim().min(1).max(160),
    membershipLoadError: z.string().trim().min(1).max(240),
    noMemberships: z.string().trim().min(1).max(240),
    invalidWindow: z.string().trim().min(1).max(240),
    contributionLoading: z.string().trim().min(1).max(160),
    contributionError: z.string().trim().min(1).max(240),
    contributionUnavailable: z.string().trim().min(1).max(240),
    boundaryNotice: z.string().trim().min(20).max(400),
});
export const timesheetsActivityReviewCopySetting = definePlatformSetting({
    definition: {
        key: 'timesheets.copy.activity-review',
        moduleId: 'timesheets',
        title: 'Timesheets activity-review copy',
        helpText: 'Controls tenant-editable labels for the Timesheets employee/window host without changing timecard or payroll truth.',
        configurationDomain: 'copy',
        valueType: 'object',
        schemaVersion: 2,
        codedFallback: {
            navigationLabel: 'Timesheets',
            title: 'Timesheets activity review',
            description: 'Choose one employee and an exact time window for optional supporting activity summaries beside Timesheets.',
            employeeLabel: 'Employee',
            fromLabel: 'From',
            toLabel: 'Before',
            selectedEmployeeLabel: 'Selected employee',
            selectedWindowLabel: 'Review window',
            loadingMemberships: 'Loading employees…',
            membershipLoadError: 'Employees are unavailable. No activity request was made.',
            noMemberships: 'No active employees are available for this review.',
            invalidWindow: 'Choose an end after the start before reviewing activity.',
            contributionLoading: 'Loading optional activity summary…',
            contributionError: 'The optional activity summary is unavailable. Timesheets data was not changed.',
            contributionUnavailable: 'No optional activity summary module is available for this tenant.',
            boundaryNotice: 'Supporting activity is shown for review only. It cannot create, edit, approve, split, or pay a Timesheets entry.',
        },
        permittedScopes: ['platform', 'plan', 'tenant'],
        mergeStrategy: 'deep-merge',
        clientSafe: true,
        serverOnly: false,
        sensitive: false,
        tenantEditable: true,
        userOverridable: false,
        offlineEligible: false,
        safetyClass: 'ordinary',
    },
    valueSchema: timesheetsActivityReviewCopyValueSchema,
    migrations: [{
            fromVersion: 1,
            toVersion: 2,
            migrate: migrateTimesheetsActivityReviewCopyVersionOne,
        }],
});
export const timesheetsSettingRegistrations = [
    timesheetsWorkdayPolicySetting,
    timesheetsPayrollPolicySetting,
    timesheetsClockDisplaySetting,
    timesheetsActivityReviewDisplaySetting,
    timesheetsClockCopySetting,
    timesheetsMyDayCopySetting,
    timesheetsLunchReviewCopySetting,
    timesheetsCrewAllocationCopySetting,
    timesheetsLocationReviewCopySetting,
    timesheetsEntrySplitCopySetting,
    timesheetsActivityReviewCopySetting,
];
//# sourceMappingURL=timesheets-setting-definitions.js.map